import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const btn = "min-h-[44px] sm:min-h-[36px] px-2 rounded border text-[13px] sm:text-xs font-semibold text-left";
const sel = "min-h-[44px] sm:min-h-[32px] text-sm sm:text-xs border border-gray-300 rounded px-2 bg-white";

/** FLD-13 — the four destinations, same order and labels everywhere material moves in Install. */
export const MODES = [
  ["own_room", "Its own room", "Each entry goes to the room its FRS line is assigned to"],
  ["area", "An area → a room in that area", "Area only = staging; pick the room when you know it"],
  ["note", "Somewhere else (type it)", "Gang box, laydown, trailer…"],
  ["stash", "A room that already has material", "Put it with the other stuff"],
];

export const emptyDest = { mode: "own_room", room_key: "", area: "", note: "", overrides: {} };

/** One fetch per open: assigned rooms per entry, the areas inside the current filter, the
 *  locations already used on this job, and every place that already holds material. */
export function useDestinations(projectId, placementIds, viewFilter) {
  const [data, setData] = useState(null);
  const ids = (placementIds || []).join(",");
  const f = JSON.stringify(viewFilter || {});
  useEffect(() => {
    if (!projectId) return;
    const flt = JSON.parse(f);
    const csv = (v) => (Array.isArray(v) ? v.join(",") : v || "");
    api.get("/material/move/destinations", { params: { project_id: projectId, placement_ids: ids,
      scope: csv(flt.scope), building: csv(flt.building), floor: csv(flt.floor), area: csv(flt.area) } })
      .then(({ data: d }) => setData(d)).catch(() => setData({ own_rooms: [], areas: [], stashes: [], used_locations: [], rooms: [] }));
  }, [projectId, ids, f]);
  return data;
}

/** What each entry's destination resolves to right now — the preview the installer confirms. */
export function resolveDest(dest, data, entries) {
  const areas = data?.areas || [];
  const roomLabel = (rk) => (data?.rooms || []).find((r) => r.room_key === rk)?.label || "";
  const own = Object.fromEntries((data?.own_rooms || []).map((o) => [o.placement_id, o]));
  return (entries || []).map((e) => {
    const o = own[e.placement_id] || {};
    if (dest.mode === "own_room") {
      const rk = dest.overrides?.[e.placement_id] || o.room_key || "";
      return { ...e, to_label: rk ? (o.room_key === rk ? o.room_label : roomLabel(rk)) : "", to_room_key: rk,
               flagged: !rk, reason: o.reason || "no assigned FRS room — pick one", noop: !!o.already_there && rk === o.room_key, staging: false };
    }
    if (dest.mode === "area") {
      if (dest.room_key) return { ...e, to_label: roomLabel(dest.room_key), to_room_key: dest.room_key, flagged: false, staging: false };
      const a = areas.find((x) => x.area === dest.area);
      return { ...e, to_label: [dest.area, dest.note].filter(Boolean).join(" · "), to_room_key: "", flagged: !dest.area,
               staging: true, area_label: a?.label || dest.area };
    }
    if (dest.mode === "stash") {
      const s = (data?.stashes || []).find((x) => x.key === dest.stash_key);
      return { ...e, to_label: s?.label || "", to_room_key: s?.room_key || "", flagged: !s, staging: false };
    }
    return { ...e, to_label: dest.note.trim(), to_room_key: "", flagged: !dest.note.trim(), staging: false };
  });
}

export function destBody(dest, data) {
  if (dest.mode === "stash") {
    const s = (data?.stashes || []).find((x) => x.key === dest.stash_key) || {};
    return { mode: "stash", room_key: s.room_key || "", note: s.note || "" };
  }
  return { mode: dest.mode, room_key: dest.room_key, area: dest.area, note: dest.note };
}

/** The shared control. `entries` are the exact placements being moved, so "its own room"
 *  resolves per entry and a mixed selection spreads home in one action. */
export default function DestinationPicker({ data, entries = [], value, onChange, testId = "dest" }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const areas = useMemo(() => data?.areas || [], [data]);
  const area = useMemo(() => areas.find((a) => a.area === value.area) || null, [areas, value.area]);
  const resolved = useMemo(() => resolveDest(value, data, entries), [value, data, entries]);
  const flagged = resolved.filter((r) => r.flagged);

  if (!data) return <span className="text-[11px] text-gray-400">loading places…</span>;
  return (
    <div className="space-y-1.5 w-full" data-testid={`${testId}-picker`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
        {MODES.map(([m, label, hint]) => (
          <button key={m} type="button" onClick={() => set({ mode: m })} data-testid={`${testId}-mode-${m}`}
                  className={`${btn} ${value.mode === m ? "bg-blue-700 text-white border-blue-700" : "bg-white border-gray-300"}`}>
            {label}
            <span className={`block text-[10px] font-normal ${value.mode === m ? "text-blue-100" : "text-gray-500"}`}>{hint}</span>
          </button>
        ))}
      </div>

      {value.mode === "own_room" && (
        <div className="text-[12px] space-y-0.5" data-testid={`${testId}-own`}>
          {resolved.map((r) => (
            <div key={r.placement_id} className={`flex flex-wrap items-center gap-1 ${r.flagged ? "text-amber-900" : "text-gray-700"}`}
                 data-testid={`${testId}-own-${r.placement_id}`}>
              <span className="truncate max-w-[45%]">{r.description}</span>
              <span>→</span>
              {r.flagged ? (
                <>
                  <span className="px-1 rounded bg-amber-100 text-[10px] font-semibold">needs a room</span>
                  <RoomSelect data={data} value={value.overrides?.[r.placement_id] || ""} testId={`${testId}-own-room-${r.placement_id}`}
                              onChange={(rk) => set({ overrides: { ...(value.overrides || {}), [r.placement_id]: rk } })} />
                </>
              ) : <b>{r.to_label}{r.noop && <span className="ml-1 font-normal text-gray-500">(already there — skipped)</span>}</b>}
            </div>
          ))}
          {flagged.length > 0 && (
            <div className="text-[11px] text-amber-900" data-testid={`${testId}-flagged`}>
              {flagged.length} entry(ies) have no clear assigned room — pick a room for those. The rest still moves.
            </div>
          )}
        </div>
      )}

      {value.mode === "area" && (
        <div className="flex flex-wrap items-center gap-1" data-testid={`${testId}-area`}>
          <select className={sel} value={value.area} data-testid={`${testId}-area-select`}
                  onChange={(e) => set({ area: e.target.value, room_key: "" })}>
            <option value="">Pick an area…</option>
            {areas.map((a) => <option key={a.key} value={a.area}>{a.label} ({a.rooms.length} room{a.rooms.length === 1 ? "" : "s"})</option>)}
          </select>
          <select className={`${sel} max-w-[240px]`} value={value.room_key} disabled={!area} data-testid={`${testId}-area-room`}
                  onChange={(e) => set({ room_key: e.target.value })}>
            <option value="">— the area itself (staging) —</option>
            {(area?.rooms || []).map((r) => <option key={r.room_key} value={r.room_key}>{r.label}</option>)}
          </select>
          {value.area && !value.room_key && (
            <span className="w-full text-[11px] text-amber-900 font-medium" data-testid={`${testId}-staging-note`}>
              Staging location — this material has NOT been spread into its assigned rooms.
            </span>
          )}
        </div>
      )}

      {value.mode === "note" && (
        <div className="flex flex-wrap items-center gap-1" data-testid={`${testId}-note`}>
          <input className={`${sel} flex-1 min-w-[200px]`} value={value.note} list={`${testId}-used`} placeholder="north stairwell landing"
                 onChange={(e) => set({ note: e.target.value })} data-testid={`${testId}-note-input`} />
          <datalist id={`${testId}-used`}>{(data.used_locations || []).map((l) => <option key={l} value={l} />)}</datalist>
          <span className="w-full text-[11px] text-gray-500">Pick from the list if it is already used on this job — same spelling, one location.</span>
        </div>
      )}

      {value.mode === "stash" && (
        <div className="space-y-0.5" data-testid={`${testId}-stash`}>
          {(data.stashes || []).length === 0 && <div className="text-[11px] text-gray-500">Nothing else is on site yet.</div>}
          {(data.stashes || []).map((s) => (
            <button key={s.key} type="button" onClick={() => set({ stash_key: s.key })} data-testid={`${testId}-stash-${s.key.replace(/[^\w]+/g, "_")}`}
                    className={`${btn} w-full flex items-center gap-2 ${value.stash_key === s.key ? "bg-blue-700 text-white border-blue-700" : "bg-white border-gray-300"}`}>
              <span className="truncate">{s.label}</span>
              <span className="ml-auto tabular-nums">({s.qty})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Never a flat alphabetical list: rooms are grouped under their area. */
export function RoomSelect({ data, value, onChange, testId }) {
  return (
    <select className={`${sel} max-w-[220px]`} value={value} data-testid={testId} onChange={(e) => onChange(e.target.value)}>
      <option value="">Pick a room…</option>
      {(data?.areas || []).map((a) => (
        <optgroup key={a.key} label={a.label}>
          {a.rooms.map((r) => <option key={r.room_key} value={r.room_key}>{r.label}</option>)}
        </optgroup>
      ))}
    </select>
  );
}
